import * as React from "react";
import { cn } from "@/lib/utils";
import { cssVar, designTokens, type CssVariableName, type DesignTokens } from "./tokens";

export const THEME_CLASS = "theme-99-problems";

export interface ThemeContextValue {
  tokens: DesignTokens;
  /** Root class applied by the provider. */
  className: typeof THEME_CLASS;
  /** `var(--name)` reference for inline styles. */
  cssVar: (name: CssVariableName, fallback?: string) => string;
  /** True when rendered inside a <ThemeProvider>; hooks still work without one. */
  hasProvider: boolean;
}

const defaultValue: ThemeContextValue = {
  tokens: designTokens,
  className: THEME_CLASS,
  cssVar,
  hasProvider: false,
};

export const ThemeContext = React.createContext<ThemeContextValue>(defaultValue);

export interface ThemeProviderProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

/**
 * Root of a "99 Problems" surface: Lora body text in gold on the black page
 * gradient, Cinzel headings. The CSS variables themselves live on :root, so
 * design-system components also render correctly outside a provider.
 */
export function ThemeProvider({ children, className, ...props }: ThemeProviderProps) {
  const value = React.useMemo<ThemeContextValue>(
    () => ({ ...defaultValue, hasProvider: true }),
    [],
  );
  return (
    <ThemeContext.Provider value={value}>
      <div className={cn(THEME_CLASS, className)} {...props}>
        {children}
      </div>
    </ThemeContext.Provider>
  );
}

/**
 * Marks a subtree that already renders the site header and footer (the app's
 * <Layout>). <PageTemplate> reads it to avoid rendering a second set.
 */
export const AppShellContext = React.createContext(false);

export function AppShellProvider({ children }: { children: React.ReactNode }) {
  return <AppShellContext.Provider value>{children}</AppShellContext.Provider>;
}
